import type { Severity } from "../types";

export const CHART_PALETTE = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
] as const;

export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#f59e0b",
  // Blue, not the emerald QuadrantDashboard uses for a healthy SCORE band: here `low` means
  // low-severity findings, and the Findings-by-Severity widget paints those blue.
  low: "#3b82f6",
  info: "#6366f1",
};

/** The bucket for findings the API keys `unspecified` and the widget prints as "No Severity". */
export const NO_SEVERITY_COLOR = "#94a3b8";

/** Dropped before matching: decoration the model adds around the severity word. */
const LABEL_NOISE = new Set(["severity"]);

/** Every spelling of the empty bucket we have seen the API or the model produce. */
const NO_SEVERITY_KEYS = new Set([
  "unspecified",
  "no",
  "none",
  "unset",
  "not set",
  "unknown",
  "n a",
]);

/**
 * Resolve a chart category (or series) label to its severity colour, or `undefined` when the label
 * is not a severity at all so the caller falls through to the positional palette.
 *
 * Matching is on whole tokens, never substrings: "Critical (3)" and "Severity: Medium" resolve, but
 * "Low latency services" and "Critical Systems Group" must not — they are ordinary categories that
 * merely contain a severity word, and colouring them would be worse than the bug this fixes.
 */
export function severityColorForLabel(label: string | number | undefined | null): string | undefined {
  if (typeof label !== "string") return undefined;

  const tokens = label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !LABEL_NOISE.has(t) && !/^\d+$/.test(t));

  const key = tokens.join(" ");
  if (!key) return undefined;
  if (key in SEVERITY_COLOR) return SEVERITY_COLOR[key as Severity];
  if (NO_SEVERITY_KEYS.has(key)) return NO_SEVERITY_COLOR;
  return undefined;
}

/**
 * Pass / fail / unknown, the platform's other status vocabulary.
 *
 * <h3>Why these exact values</h3>
 * They are what the Drift Management screen already says: its control badges are green / red /
 * grey (`CTRL_STATUS`), its scan bar chart is emerald / rose / gray, and the backend declares the
 * same ramp load-bearing in `ChartSpecs.VERDICT_RAMP`. A chat chart summarising that screen's data
 * has to agree with it — a reader who sees orange for "Passed" beside a green badge for the same
 * control does not conclude the palette is decorative, they conclude one of the two is wrong.
 *
 * Without this map a status chart falls through to the *positional* palette, which is worse than
 * arbitrary: the hue depends on which status happens to appear first in the rows, so it changes
 * between two questions over the same data.
 */
export const VERDICT_COLOR: Record<string, string> = {
  passed: "#22c55e",
  pass: "#22c55e",
  failed: "#ef4444",
  fail: "#ef4444",
};

/**
 * Resolve a status label to its verdict colour, or `undefined` when it is not a verdict.
 *
 * Whole-token matching for the same reason {@link severityColorForLabel} uses it: "Failed" and
 * "Failed (3)" resolve, "Failed login attempts" is an ordinary category and must not.
 * `unknown` and its spellings are deliberately left to {@link severityColorForLabel}, which already
 * maps them onto {@link NO_SEVERITY_COLOR} — one grey for "no verdict", wherever it comes from.
 */
export function verdictColorForLabel(label: string | number | undefined | null): string | undefined {
  if (typeof label !== "string") return undefined;

  const key = label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !/^\d+$/.test(t))
    .join(" ");

  return key ? VERDICT_COLOR[key] : undefined;
}

/**
 * Slice/series colour, in precedence order: an explicit colour the tool sent, then the row's own
 * `severity` field, then the severity read off its label, and only then the positional palette.
 */
export function resolveColor(
  source: { color?: string; severity?: Severity; label?: string | number | null } | undefined,
  index: number,
): string {
  if (source?.color) return source.color;
  if (source?.severity && source.severity in SEVERITY_COLOR) {
    return SEVERITY_COLOR[source.severity];
  }
  const byLabel = severityColorForLabel(source?.label);
  if (byLabel) return byLabel;
  return CHART_PALETTE[index % CHART_PALETTE.length];
}

export function shade(hex: string, amt: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const target = amt < 0 ? 0 : 255;
  const p = Math.min(1, Math.abs(amt));
  const mix = (ch: number) => Math.round((target - ch) * p + ch);
  const r = mix((num >> 16) & 255);
  const g = mix((num >> 8) & 255);
  const b = mix(num & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export const AXIS_TICK = { fontSize: 11, fontWeight: 500 } as const;

export const ANIM_MS = 600;

/*
 * Radial motion/geometry is no longer a copy of the dashboard card's — it now IS the dashboard card's.
 * Both surfaces import components/charts/donutCore, so the chat donut and the GRC dashboard donut
 * cannot drift apart again. Re-exported under the RADIAL_* names the chat charts already import.
 *
 * These stay separate from ANIM_MS, which CartesianChart also uses — retargeting that
 * constant would silently change bar/line/radar timing too.
 */
export {
  DONUT_ANIM_MS as RADIAL_ANIM_MS,
  DONUT_ANIM_EASING as RADIAL_ANIM_EASING,
  DONUT_START_ANGLE as RADIAL_START_ANGLE,
  DONUT_END_ANGLE as RADIAL_END_ANGLE,
  DONUT_HOVER_POP_PX as RADIAL_HOVER_POP_PX,
} from "../../charts/donutCore";

export const CHART_HEIGHT = {
  cartesian: 208,
  radial: 176,
  radar: 208,
} as const;

/**
 * Plot height handed to the platform donut for a chat doughnut.
 *
 * The same 220 the Risk Quadrant's findings-by-severity panel passes, and matching it is the point:
 * the donut derives its label margin and its radius from this number, so an equal height reproduces
 * that panel's geometry exactly rather than approximately. It is deliberately taller than
 * {@link CHART_HEIGHT}.radial — leader labels are drawn outside the arc and a short plot squeezes
 * both the ring and the room its labels need.
 */
export const DOUGHNUT_PLOT_PX = 220;

/** Legend row height under the donut, for reserving a doughnut's height before it renders. */
export const DOUGHNUT_LEGEND_ROW_PX = 24;

export const CURSOR_FILL = "rgba(148, 163, 184, 0.16)";
export const CURSOR_LINE = "rgba(148, 163, 184, 0.55)";

/**
 * Recharts overrides applied to every chat chart's wrapper.
 *
 * <h3>These win over whatever the chart component asked for</h3>
 * Recharts sets `stroke` as an SVG *presentation attribute*, and a CSS declaration beats a
 * presentation attribute — so a component's own `stroke={gridColor}` never takes effect inside the
 * chat panel. Every companion chart's grid is whatever this list says, which is why the axis and
 * grid colours have to be right here rather than in each renderer.
 *
 * <h3>Why the grid keys off `--muted-foreground`, not `--border`</h3>
 * `--border` is a hairline token tuned for the seam between two adjacent surfaces: `#e2e8f0` in
 * light, `rgba(255,255,255,0.07)` in dark. At the 60% these rules used to apply, that lands around
 * 1.1:1 against a white card — a gridline nobody can see, which is exactly what was reported.
 * `--muted-foreground` is a *content* token (`#475569` / `#9fadc4`), so it carries real contrast
 * against its own background in both themes and stays quiet enough not to compete with the marks.
 */
export const CHART_THEME_CLASS = [
  "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground",
  "[&_.recharts-cartesian-axis-line]:stroke-muted-foreground/40",
  "[&_.recharts-cartesian-grid_line]:stroke-muted-foreground/25",
  "[&_.recharts-polar-grid_line]:stroke-muted-foreground/25",
  "[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-foreground/[0.04]",
  "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border",
  "[&_.recharts-sector[stroke='#fff']]:stroke-transparent",
  "[&_.recharts-layer]:outline-none",
  "[&_.recharts-surface]:outline-none",
].join(" ");

export function formatValue(v: number | string | null | undefined, unit = ""): string {
  const n = Number(v);
  return `${(Number.isFinite(n) ? n : 0).toLocaleString()}${unit}`;
}

export function formatCompact(v: number | string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${n}`;
}

// Truncates a long axis-tick label; the full value stays available in the tooltip.
export function truncateLabel(v: string | number, max = 14): string {
  const s = String(v);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
